import Reveal from "@/components/Reveal";
import { TrendingUp, ArrowDown, ArrowUp, FileText } from "lucide-react";

const RESULTS = [
  {
    num: "01",
    tagline: "A stronger first 45 days",
    desc: "Professional presentation, built for extended stays",
    before: "$2,100",
    beforeLabel: "Traditional monthly rent",
    after: "$5,200",
    afterLabel: "Gross revenue in first 45 days",
    delta: "+$2,050",
    deltaLabel: "gross increase over comparable 45-day period",
    pct: "+65%",
  },
  {
    num: "02",
    tagline: "More monthly income potential",
    desc: "A welcoming, furnished room for working travelers",
    before: "$1,900",
    beforeLabel: "Traditional monthly rent",
    after: "$3,900",
    afterLabel: "Monthly gross revenue",
    delta: "+$2,000",
    deltaLabel: "gross increase per month",
    pct: "+105%",
  },
  {
    num: "03",
    tagline: "A significant revenue difference",
    desc: "Guest-ready comfort meets full-service operations",
    before: "$1,800",
    beforeLabel: "Traditional monthly rent",
    after: "$4,900",
    afterLabel: "Monthly gross revenue",
    delta: "+$3,100",
    deltaLabel: "gross increase per month",
    pct: "+172%",
  },
];

export default function Results() {
  return (
    <section id="results" className="py-20 sm:py-28 bg-[#222222]" data-testid="results-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal className="text-center">
          <p className="text-sm font-bold tracking-[0.2em] uppercase text-[#FFD3C6]">Our Work</p>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-white">
            Recent Landlord Partnership Results
          </h2>
          <p className="mt-4 text-base sm:text-lg text-white/60 max-w-2xl mx-auto">
            More rental income potential. Less day-to-day work for the owner.
          </p>
        </Reveal>

        <div className="mt-12 grid md:grid-cols-3 gap-6 sm:gap-8">
          {RESULTS.map((r, i) => (
            <Reveal key={r.num} delay={i * 0.1}>
              <article
                data-testid={`result-card-${r.num}`}
                className="h-full bg-white/[0.06] border border-white/10 rounded-2xl p-8 backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:bg-white/[0.09]"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-bold tracking-widest uppercase text-white/40">Property {r.num}</p>
                    <h3 className="mt-2 text-xl font-bold text-white">{r.tagline}</h3>
                    <p className="mt-1 text-sm text-white/55">{r.desc}</p>
                  </div>
                  <span className="shrink-0 inline-flex items-center gap-1 bg-[#FF5A5F] text-white text-sm font-extrabold px-3 py-1.5 rounded-full">
                    <TrendingUp className="w-4 h-4" />
                    {r.pct}
                  </span>
                </div>

                <div className="mt-7 space-y-4">
                  <div className="flex items-center gap-4">
                    <span className="shrink-0 w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                      <ArrowDown className="w-4 h-4 text-white/50" />
                    </span>
                    <div>
                      <p className="font-heading text-2xl font-extrabold text-white/60 line-through decoration-white/30">{r.before}</p>
                      <p className="text-xs font-semibold text-white/40 uppercase tracking-wide">{r.beforeLabel}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="shrink-0 w-8 h-8 rounded-full bg-[#FF5A5F]/20 flex items-center justify-center">
                      <ArrowUp className="w-4 h-4 text-[#FF8A8E]" />
                    </span>
                    <div>
                      <p className="font-heading text-3xl font-extrabold text-white">{r.after}</p>
                      <p className="text-xs font-semibold text-[#FFD3C6] uppercase tracking-wide">{r.afterLabel}</p>
                    </div>
                  </div>
                  <div className="pt-4 border-t border-white/10">
                    <p className="text-lg font-bold text-[#FF8A8E]">{r.delta}</p>
                    <p className="text-xs text-white/45 mt-0.5">{r.deltaLabel}</p>
                  </div>
                </div>

                <p className="mt-6 pt-5 border-t border-white/10 text-xs font-semibold text-white/50 tracking-wide">
                  Guest-ready presentation · Marketing · Guest support · Cleaning &amp; upkeep
                </p>
              </article>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-10 text-center">
          <a
            href="/shonta-solutions-results.pdf"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="results-pdf-button"
            className="inline-flex items-center gap-2 bg-[#FF5A5F] hover:bg-[#E0484D] text-white font-bold px-8 py-4 rounded-full transition-all duration-200 shadow-[0_8px_24px_rgba(255,90,95,0.35)] hover:-translate-y-0.5"
          >
            <FileText className="w-5 h-5" />
            View the Full Results Report (PDF)
          </a>
          <p className="mt-6 text-xs sm:text-sm text-white/40 max-w-3xl mx-auto" data-testid="results-disclaimer">
            Figures reflect individual properties over specific periods. Rental performance varies by market,
            season, and property — these results are shared for transparency and are not a guarantee of future income.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
